import { MarketId } from '@velocity-exchange/common';
import { BigNum } from '@velocity-exchange/sdk';
import { IfData } from 'src/utils/insuranceFund';
import { InsuranceFundListData, HeaderKey } from './types';
import { SortDirection } from 'src/components/Tables/SortCaret';

export const buildRowData = (
	marketIndex: number,
	poolId: number,
	getOraclePrice: (marketId: MarketId) => BigNum | undefined,
	ifData: IfData | undefined
): InsuranceFundListData => {
	const oraclePrice = getOraclePrice(MarketId.createSpotMarket(marketIndex));

	if (!oraclePrice || !ifData) {
		return {
			marketIndex,
			vaultBalance: 0,
			apy: 0,
			cumulativeEarningsBase: 0,
			cumulativeEarningsQuote: 0,
			yourBalanceBase: 0,
			yourBalanceQuote: 0,
			poolId,
			ifData,
		};
	}

	const vaultBalance = (ifData.vault.totalStake ?? 0) * oraclePrice.toNum();

	const userEarnings =
		(ifData.userStake?.currentStake ?? 0) - (ifData.userStake?.costBasis ?? 0);

	const cumulativeEarningsQuote = userEarnings * oraclePrice.toNum();

	const yourBalanceBase = ifData.userStake?.currentStake ?? 0;
	const yourBalanceQuote =
		ifData.userStake?.currentStakeNotionalValue?.toNum() ?? 0;

	return {
		marketIndex,
		vaultBalance: vaultBalance,
		apy: ifData.vault.sevenDayApy ?? 0,
		yourBalanceBase: yourBalanceBase,
		yourBalanceQuote: yourBalanceQuote,
		cumulativeEarningsBase: userEarnings,
		cumulativeEarningsQuote: cumulativeEarningsQuote,
		poolId,
		ifData,
	};
};

export const getSortedData = (
	filteredRowData: InsuranceFundListData[],
	sortState: SortDirection,
	currentSortHeader: HeaderKey
) => {
	let data = [...filteredRowData];

	if (sortState === 'neutral') {
		return data.sort((a, b) => {
			const balanceDiff = b.yourBalanceQuote - a.yourBalanceQuote;
			if (balanceDiff !== 0) return balanceDiff;

			const vaultDiff = b.vaultBalance - a.vaultBalance;
			if (vaultDiff !== 0) return vaultDiff;

			return a.poolId - b.poolId;
		});
	}

	data = data.sort((a, b) => {
		let valA: number;
		let valB: number;

		switch (currentSortHeader) {
			case 'vaultBalance':
				valA = a.vaultBalance;
				valB = b.vaultBalance;
				break;
			case 'apy':
				valA = a.apy;
				valB = b.apy;
				break;
			case 'cumulativeEarnings':
				valA = a.cumulativeEarningsQuote;
				valB = b.cumulativeEarningsQuote;
				break;
			case 'currentStake':
				valA = a.yourBalanceQuote;
				valB = b.yourBalanceQuote;
				break;
			default:
				return 0;
		}

		if (sortState === 'ascending') {
			return valA - valB;
		} else {
			return valB - valA;
		}
	});

	return data;
};
